import { useState, useRef, useCallback, useEffect } from 'react';
import {
    TranscribeStreamingClient,
    StartMedicalStreamTranscriptionCommand,
    MedicalContentIdentificationType,
} from '@aws-sdk/client-transcribe-streaming';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { useAuthContext } from '../context/AuthContext';
import { getApiBaseUrl, isStaging } from '../utils/api';
import { decodeToken } from '../utils/token';

// PCM Audio encoding helpers
const pcmEncode = (input: Float32Array) => {
    let offset = 0;
    const buffer = new ArrayBuffer(input.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
};

// Hardcoded for now based on typical params, ideally comes from config
// This needs to match the Identity Pool ID created in infra.yaml
// Since it's environment specific, we might need a better way to inject this.
// For now, I will use a placeholder that needs to be replaced or passed in.
const REGION = 'us-west-2';

// We need to fetch the Identity Pool ID. 
// Ideally, this is passed from the backend or baked into the build.
// For this implementation, I'll attempt to derive it or assume it's available globally/via env.
// Given the constraints, I will add a TODO and use a hardcoded value if available, 
// or implement a fetch mechanism if I can.
//
// However, since I just added it to the infrastructure, I don't have the ID yet.
// I will implement the hook to accept the ID or look for it in a specific place.
// Let's assume for now we might need to manually update this file after deployment 
// OR we can make it an argument to the hook if we fetch it elsewhere.
//
const STAGING_IDENTITY_POOL_ID = 'us-west-2:45cb712c-4ee0-4332-84a0-813d81cc78da';
// FIXME: Update this with the Production Identity Pool ID after deploying to prod.
const PROD_IDENTITY_POOL_ID = 'us-west-2:baf89525-1cab-47b8-b293-cd8fcb96f8b2';

const getIdentityPoolId = () => {
    // Use the existing utility to check for staging environment.
    // This respects ?stagingEnv=true query param.
    // If your production environment does NOT use query params, you might need 
    // to check hostname (e.g. if window.location.hostname === 'staging-api.precisca.ai')
    // But sticking to the existing api.ts pattern:
    const isStagingEnv = isStaging();
    return isStagingEnv ? STAGING_IDENTITY_POOL_ID : PROD_IDENTITY_POOL_ID;
};

export const useTranscribeMedical = () => {
    const { accessToken } = useAuthContext(); // Get the token from context
    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [error, setError] = useState<string | null>(null);

    const clientRef = useRef<TranscribeStreamingClient | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

    const isStreamingRef = useRef(false);
    const sessionIdRef = useRef<string | null>(null);

    // We need to clean up on unmount
    useEffect(() => {
        return () => {
            stopRecording();
        };
    }, []);

    const startRecording = useCallback(async (onTranscriptUpdate: (text: string) => void) => {
        setError(null);
        setTranscript('');
        isStreamingRef.current = true;
        const sessionId = Math.random().toString(36).substring(7);
        sessionIdRef.current = sessionId;

        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('Microphone access is not supported in this browser.');
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                sampleRate: 44100,
            });
            audioContextRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);
            sourceRef.current = source;

            // Resample down to 16000Hz (Transcribe preferred) not strictly required but good practice.
            // Actually Transcribe supports higher sample rates now, but let's stick to standard handling.
            // We will perform naive downsampling or just send at default if supported.
            // Let's use 44.1kHz or 48kHz as is and let AudioContext handle it, 
            // but Transcribe Streaming often expects specific rates for best results.
            // We'll stick to sending the chunks as they come from the processor.

            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            source.connect(processor);
            processor.connect(audioContext.destination);

            const identityPoolId = getIdentityPoolId();

            // Check for placeholder ID
            if (identityPoolId.includes('REPLACE_WITH_PROD_IDENTITY_POOL_ID')) {
                const msg = "Production AWS Identity Pool ID is not configured. Please update `PROD_IDENTITY_POOL_ID` in `frontend/src/hooks/useTranscribeMedical.ts`.";
                console.error(msg);
                setError(msg);
                setIsRecording(false);
                isStreamingRef.current = false;
                return;
            }



            // Initialize Client
            const client = new TranscribeStreamingClient({
                region: REGION,
                credentials: fromCognitoIdentityPool({
                    clientConfig: { region: REGION },
                    identityPoolId: identityPoolId,
                }),
            });
            clientRef.current = client;

            // Create an async generator for audio stream
            const audioStream = async function* () {
                while (isRecording || (processorRef.current && isRecording === false)) {
                    // This logic is a bit tricky with ScriptProcessor.
                    // We need to bridge the event-based ScriptProcessor with the async generator.
                    // A safer way is to push chunks to a queue and yield them.
                    await new Promise(r => setTimeout(r, 100));
                }
            };

            // ALTERNATIVE: Use a Passthrough stream or similar structure.
            // Since we can't easily make a generator from an event listener directly in this scope
            // without a queue, let's use a queue.

            const audioQueue: Uint8Array[] = [];

            processor.onaudioprocess = (e) => {
                // Determine if we are still the active session
                if (sessionIdRef.current !== sessionId) return;

                const inputData = e.inputBuffer.getChannelData(0);
                // Downsample logic/encoding
                const encoded = pcmEncode(inputData);
                audioQueue.push(new Uint8Array(encoded));
            };

            const audioGenerator = async function* () {
                while (isStreamingRef.current && sessionIdRef.current === sessionId) {
                    if (audioQueue.length > 0) {
                        const chunk = audioQueue.shift();
                        if (chunk) {
                            yield { AudioEvent: { AudioChunk: chunk } };
                        }
                    } else {
                        // Wait a bit to avoid hot loop
                        await new Promise(resolve => setTimeout(resolve, 20));
                    }
                }
            };

            const command = new StartMedicalStreamTranscriptionCommand({
                LanguageCode: 'en-US',
                MediaEncoding: 'pcm',
                MediaSampleRateHertz: 44100, // Match AudioContext
                Specialty: 'ONCOLOGY',
                Type: 'DICTATION',
                AudioStream: audioGenerator(),
            });

            setIsRecording(true);

            // Start the request
            const response = await client.send(command);

            // Handle the response stream
            (async () => {
                try {
                    if (response.TranscriptResultStream) {
                        for await (const event of response.TranscriptResultStream) {
                            // Enforce session isolation: abort if this is a zombie session
                            if (sessionIdRef.current !== sessionId) return;

                            if (event.TranscriptEvent) {
                                const results = event.TranscriptEvent.Transcript?.Results;
                                if (results && results.length > 0) {
                                    const result = results[0];
                                    // Only process if the result is final (not partial)
                                    // This prevents duplication of text in the UI
                                    if (!result.IsPartial && result.Alternatives && result.Alternatives.length > 0) {
                                        const transcriptText = result.Alternatives[0].Transcript;
                                        if (transcriptText) {
                                            onTranscriptUpdate(transcriptText);
                                        }
                                    }
                                }
                            }
                        }
                    }
                } catch (err) {
                    // Only report error if we are still the active session
                    if (sessionIdRef.current === sessionId) {
                        console.error("Stream error:", err);
                        setError("Connection to transcription service lost.");
                        stopRecording();
                    }
                }
            })();

        } catch (err: any) {
            console.error('Error starting transcription:', err);
            setError(err.message || 'Failed to start recording');
            setIsRecording(false);
            isStreamingRef.current = false;
        }
    }, []);

    const stopRecording = useCallback(() => {
        setIsRecording(false);
        isStreamingRef.current = false;
        sessionIdRef.current = null; // Kill any active session

        if (sourceRef.current) {
            sourceRef.current.disconnect();
            sourceRef.current = null;
        }
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current.onaudioprocess = null;
            processorRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        // Client doesn't strictly need closing but good to clear
        clientRef.current = null;
    }, []);

    return {
        startRecording,
        stopRecording,
        isRecording,
        error,
    };
};
